import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export async function exportPlanToZip(plan: any, brandName: string) {
  const zip = new JSZip();
  const folderName = `${brandName}_${plan.month}_${plan.year}`.replace(/[^a-z0-9]/gi, '_');
  const folder = zip.folder(folderName);
  
  if (!folder) throw new Error("Could not create zip folder");

  const mediaFolder = folder.folder("media");
  if (!mediaFolder) throw new Error("Could not create media folder");

  // Add a manifest/summary file
  const summary = `
Content Plan for ${brandName}
Month: ${plan.month} ${plan.year}
Generated on: ${new Date().toLocaleString()}

Posts:
${plan.calendar.map((p: any, i: number) => `
Post ${i + 1}: ${p.title}
Platform: ${p.platform}
Description: ${p.description}
Media Items: ${p.slides?.length || 0}
`).join('\n')}
  `;
  folder.file("summary.txt", summary);

  // Fetch and add all media
  const mediaPromises: Promise<void>[] = [];
  plan.calendar.forEach((post: any, pIdx: number) => {
    (post.slides || []).forEach((slide: any, sIdx: number) => {
      if (slide.url) {
        const promise = (async () => {
          try {
            const response = await fetch(slide.url);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const blob = await response.blob();
            
            // Try to detect extension from content type if not provided
            let ext = slide.isVideo ? 'mp4' : 'png';
            const contentType = response.headers.get('content-type');
            if (contentType) {
              if (contentType.includes('video/')) ext = contentType.split('/')[1] || 'mp4';
              else if (contentType.includes('image/')) ext = contentType.split('/')[1] || 'png';
            } else if (slide.url.toLowerCase().endsWith('.png')) {
               ext = 'png';
            } else if (slide.url.toLowerCase().endsWith('.jpg') || slide.url.toLowerCase().endsWith('.jpeg')) {
               ext = 'jpg';
            } else if (slide.url.toLowerCase().endsWith('.mp4')) {
               ext = 'mp4';
            }
            
            const fileName = `post_${pIdx + 1}_slide_${sIdx + 1}.${ext}`;
            mediaFolder.file(fileName, blob);
          } catch (err) {
            console.warn(`Failed to export media ${slide.url}`, err);
          }
        })();
        mediaPromises.push(promise);
      }
    });
  });

  await Promise.all(mediaPromises);
  const content = await zip.generateAsync({ type: "blob" });
  saveAs(content, `${folderName}_Assets.zip`);
}

export async function exportPlanToPDF(elementId: string, brandName: string, fileName: string) {
  const element = document.getElementById(elementId);
  if (!element) throw new Error("Summary element not found");

  // Temporarily show the element if it was hidden (though we usually have it hidden off-screen)
  const originalStyle = element.style.cssText;
  element.style.position = 'relative';
  element.style.left = '0';
  element.style.visibility = 'visible';
  element.style.display = 'block';

  const canvas = await html2canvas(element, {
    useCORS: true,
    scale: 1.5, // Reduced scale for file size (1.5 is plenty for print/view)
    logging: false,
    backgroundColor: '#ffffff'
  });

  // Restore original styles
  element.style.cssText = originalStyle;

  const imgData = canvas.toDataURL('image/jpeg', 0.8); // Use JPEG with 0.8 quality for smaller size
  const pdf = new jsPDF('p', 'mm', 'a4');
  
  const imgWidth = 210; // A4 width
  const pageHeight = 297; // A4 height
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = 0;

  // Add first page
  pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  // Continue adding pages if necessary
  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  pdf.save(`${fileName}.pdf`);
}
